import { useCallback, useEffect, useRef, useState } from "react";

import useApi from "@/components/useApi";
import { ANALYTICS_RESOURCE } from "@/pages/CreatorAnalytics/constants";

import type { ICharacterPerformance, ICharacterTimeseriesPoint, ICreatorSummary, IDateRange, ITimeseriesPoint } from "./types";
import { toLocalDateStr } from "./utils";

interface IUseCreatorAnalyticsResult {
  summary: ICreatorSummary | null;
  timeseries: ITimeseriesPoint[];
  characters: ICharacterPerformance[];
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

interface IUseCreatorAnalyticsOptions {
  shouldIncludeOverviewTimeseries?: boolean;
}

export const useCreatorAnalytics = (
  dateRange: IDateRange,
  creatorId?: string,
  options: IUseCreatorAnalyticsOptions = {},
): IUseCreatorAnalyticsResult => {
  const shouldIncludeOverviewTimeseries = options.shouldIncludeOverviewTimeseries ?? true;
  const { jsonApiService, callApi } = useApi();
  const [stateSummary, setStateSummary] = useState<ICreatorSummary | null>(null);
  const [stateTimeseries, setStateTimeseries] = useState<ITimeseriesPoint[]>([]);
  const [stateCharacters, setStateCharacters] = useState<ICharacterPerformance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stateError, setStateError] = useState<string | null>(null);
  const refRequestId = useRef(0);

  const fetchAll = useCallback(async () => {
    const currentRequestId = ++refRequestId.current;
    setIsLoading(true);
    setStateError(null);
    setStateSummary(null);
    setStateTimeseries([]);
    setStateCharacters([]);

    const from = toLocalDateStr(dateRange.from);
    const to = toLocalDateStr(dateRange.to);
    const params: Record<string, string> = { from, to };
    if (creatorId) params.creatorId = creatorId;

    try {
      const [summaryResult, timeseriesResult, charactersResult] = await Promise.all([
        callApi(jsonApiService.fetchResource, ANALYTICS_RESOURCE.summary, null, params) as Promise<ICreatorSummary>,
        shouldIncludeOverviewTimeseries
          ? (callApi(jsonApiService.fetchResource, ANALYTICS_RESOURCE.timeseries, null, params) as Promise<ITimeseriesPoint[]>)
          : Promise.resolve([] as ITimeseriesPoint[]),
        callApi(jsonApiService.fetchResource, ANALYTICS_RESOURCE.characters, null, params) as Promise<ICharacterPerformance[]>,
      ]);

      if (currentRequestId !== refRequestId.current) return;

      setStateSummary(summaryResult ?? null);
      setStateTimeseries(timeseriesResult ?? []);
      setStateCharacters(charactersResult ?? []);
    } catch (caught) {
      if (currentRequestId !== refRequestId.current) return;
      setStateError(caught instanceof Error ? caught.message : "Failed to load analytics");
    } finally {
      if (currentRequestId === refRequestId.current) {
        setIsLoading(false);
      }
    }
  }, [dateRange, jsonApiService, callApi, creatorId, shouldIncludeOverviewTimeseries]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const refetch = useCallback(() => {
    void fetchAll();
  }, [fetchAll]);

  return {
    summary: stateSummary,
    timeseries: stateTimeseries,
    characters: stateCharacters,
    isLoading,
    error: stateError,
    refetch,
  };
};

interface IUseCharacterTimeseriesResult {
  timeseries: ICharacterTimeseriesPoint[];
  isLoading: boolean;
  error: string | null;
}

export const useCharacterTimeseries = (characterId: string | undefined, dateRange: IDateRange, creatorId?: string): IUseCharacterTimeseriesResult => {
  const { jsonApiService, callApi } = useApi();
  const [stateTimeseries, setStateTimeseries] = useState<ICharacterTimeseriesPoint[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [stateError, setStateError] = useState<string | null>(null);
  const refRequestId = useRef(0);

  const fetchData = useCallback(async () => {
    if (!characterId) {
      setStateTimeseries([]);
      setIsLoading(false);
      return;
    }

    const currentRequestId = ++refRequestId.current;
    setIsLoading(true);
    setStateError(null);
    setStateTimeseries([]);

    const from = toLocalDateStr(dateRange.from);
    const to = toLocalDateStr(dateRange.to);
    const params: Record<string, string> = { from, to };
    if (creatorId) params.creatorId = creatorId;

    try {
      const result = (await callApi(
        jsonApiService.fetchResource,
        ANALYTICS_RESOURCE.characterTimeseries(characterId),
        null,
        params,
      )) as ICharacterTimeseriesPoint[];

      if (currentRequestId !== refRequestId.current) return;
      setStateTimeseries(result ?? []);
    } catch (caught) {
      if (currentRequestId !== refRequestId.current) return;
      setStateError(caught instanceof Error ? caught.message : "Failed to load character data");
    } finally {
      if (currentRequestId === refRequestId.current) {
        setIsLoading(false);
      }
    }
  }, [characterId, dateRange, jsonApiService, callApi, creatorId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return {
    timeseries: stateTimeseries,
    isLoading,
    error: stateError,
  };
};
